import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Link as LinkIcon, FileText, Upload, Mail, Phone, Download, Camera, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const StaffForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    current_club: '',
    department: '',
    email: '',
    phone: '',
    photo: '',
    date_of_birth: '',
    contract_end_date: '',
    salary: '',
    salary_frequency: 'yearly',
    notes: '',
    links: [],
    documents: []
  });

  const [newLink, setNewLink] = useState({ name: '', url: '' });
  const [newDocumentName, setNewDocumentName] = useState('');

  useEffect(() => {
    if (isEdit) {
      fetchStaff();
    }
  }, [id]);

  const fetchStaff = async () => {
    try {
      const response = await api.get(`/staff/${id}`);
      setFormData({
        ...response.data,
        email: response.data.email || '',
        phone: response.data.phone || '',
        photo: response.data.photo || '',
        department: response.data.department || '',
        notes: response.data.notes || '',
        links: response.data.links || [],
        documents: response.data.documents || []
      });
    } catch (error) {
      toast.error('Failed to load staff member');
      navigate('/staff');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        salary: formData.salary ? parseFloat(formData.salary) : null
      };
      if (isEdit) {
        await api.put(`/staff/${id}`, payload);
        toast.success('Staff member updated successfully');
      } else {
        await api.post('/staff', payload);
        toast.success('Staff member created successfully');
      }
      navigate('/staff');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save staff member');
    } finally {
      setLoading(false);
    }
  };

  const addLink = () => {
    if (newLink.name && newLink.url) {
      setFormData({ ...formData, links: [...formData.links, { ...newLink }] });
      setNewLink({ name: '', url: '' });
    }
  };

  const removeLink = (index) => {
    setFormData({ ...formData, links: formData.links.filter((_, i) => i !== index) });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Please upload a PDF file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File size must be less than 10MB'); return; }
    const reader = new FileReader();
    reader.onload = (event) => {
      const docName = newDocumentName || file.name.replace('.pdf', '');
      setFormData({
        ...formData,
        documents: [...formData.documents, { name: docName, filename: file.name, data: event.target.result }]
      });
      setNewDocumentName('');
      toast.success(`Document "${docName}" added`);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeDocument = (index) => {
    setFormData({ ...formData, documents: formData.documents.filter((_, i) => i !== index) });
  };

  const downloadDocument = (doc) => {
    if (doc.data) {
      const link = document.createElement('a');
      link.href = doc.data;
      link.download = doc.filename || `${doc.name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (doc.url) {
      window.open(doc.url, '_blank');
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image size must be less than 5MB'); return; }
    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData({ ...formData, photo: event.target.result });
      toast.success('Photo uploaded');
    };
    reader.readAsDataURL(file);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const removePhoto = () => {
    setFormData({ ...formData, photo: '' });
  };

  const departments = ['Manager', 'Assistant Manager', 'Coach', 'Assistant Coach', 'Physio', 'S & C', 'Other'];
  const salaryFrequencies = ['weekly', 'monthly', 'yearly'];

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <Link
            to="/staff"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span className="uppercase tracking-wide text-sm">Back to Coaches</span>
          </Link>
          <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
            {isEdit ? 'Edit Coach' : 'Add New Coach'}
          </h1>
          <p className="text-lg text-zinc-300">Enter coach details below</p>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="glass-card p-8 space-y-6">
            {/* Photo + Basic Info */}
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0">
                <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Photo</label>
                <div className="relative">
                  {formData.photo ? (
                    <div className="relative group">
                      <img src={formData.photo} alt="Staff" className="w-32 h-32 md:w-40 md:h-40 rounded-lg object-cover border-2 border-white/10" />
                      <button type="button" onClick={removePhoto} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 md:w-40 md:h-40 rounded-lg bg-zinc-900/50 border-2 border-dashed border-white/20 flex flex-col items-center justify-center">
                      <Briefcase className="w-12 h-12 text-zinc-600 mb-2" />
                      <span className="text-xs text-zinc-500">No photo</span>
                    </div>
                  )}
                </div>
                <input type="file" ref={photoInputRef} accept="image/*" onChange={handlePhotoUpload} className="hidden" id="staff-photo-upload" />
                <label htmlFor="staff-photo-upload" className="mt-3 w-full px-4 py-2 bg-white/20 text-white hover:bg-white/30 rounded-md font-bold cursor-pointer flex items-center justify-center gap-2 text-sm">
                  <Camera className="w-4 h-4" />
                  {formData.photo ? 'Change' : 'Upload'}
                </label>
              </div>

              <div className="flex-1">
                <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">Basic Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Full Name *</label>
                    <input type="text" required data-testid="staff-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="e.g., John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Current Club</label>
                    <input type="text" data-testid="staff-current-club-input" value={formData.current_club || ''} onChange={(e) => setFormData({ ...formData, current_club: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="e.g., Chelsea FC Women"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Position</label>
                    <select data-testid="staff-department-select" value={formData.department || ''} onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white outline-none transition-all duration-200"
                    >
                      <option value="">Select position</option>
                      {departments.map((dept) => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Date of Birth</label>
                    <input type="date" value={formData.date_of_birth || ''} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Contract End Date</label>
                    <input type="date" value={formData.contract_end_date || ''} onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="pt-6 border-t border-white/5">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6 flex items-center gap-2">
                <Mail className="w-6 h-6 text-blue-400" />
                Contact Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <input type="email" data-testid="staff-email-input" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="staff@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <input type="tel" data-testid="staff-phone-input" value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="+44 7XXX XXX XXX"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contract Details */}
            <div className="pt-6 border-t border-white/5">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">Contract Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Salary (£)</label>
                  <div className="flex gap-3">
                    <input type="number" step="0.01" value={formData.salary || ''} onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                      className="flex-1 bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="e.g., 50000"
                    />
                    <select value={formData.salary_frequency || 'yearly'} onChange={(e) => setFormData({ ...formData, salary_frequency: e.target.value })}
                      className="w-32 bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-3 text-white outline-none"
                    >
                      {salaryFrequencies.map((freq) => (
                        <option key={freq} value={freq}>{freq.charAt(0).toUpperCase() + freq.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Links */}
            <div className="pt-6 border-t border-white/5">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">
                <LinkIcon className="w-6 h-6 inline mr-2" />
                Links
              </h2>
              {formData.links && formData.links.length > 0 && (
                <div className="space-y-2 mb-4">
                  {formData.links.map((link, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <LinkIcon className="w-4 h-4 text-blue-400" />
                      <span className="text-white font-medium">{link.name}</span>
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm truncate flex-1">{link.url}</a>
                      <button type="button" onClick={() => removeLink(index)} className="p-1 hover:bg-red-500/20 rounded text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-3">
                <input type="text" placeholder="Link name" value={newLink.name} onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                  className="flex-1 bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-2 text-white placeholder:text-zinc-600 outline-none"
                />
                <input type="url" placeholder="URL (https://...)" value={newLink.url} onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                  className="flex-1 bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-2 text-white placeholder:text-zinc-600 outline-none"
                />
                <button type="button" onClick={addLink} className="px-4 py-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-md font-bold">
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Documents */}
            <div className="pt-6 border-t border-white/5">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">
                <FileText className="w-6 h-6 inline mr-2" />
                PDF Documents
              </h2>
              {formData.documents && formData.documents.length > 0 && (
                <div className="space-y-2 mb-4">
                  {formData.documents.map((doc, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <FileText className="w-4 h-4 text-red-400" />
                      <span className="text-white font-medium flex-1">{doc.name}</span>
                      {doc.filename && <span className="text-xs text-zinc-500">{doc.filename}</span>}
                      <button type="button" onClick={() => downloadDocument(doc)} className="p-1.5 hover:bg-green-500/20 rounded text-green-400" title="Download">
                        <Download className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => removeDocument(index)} className="p-1.5 hover:bg-red-500/20 rounded text-red-400" title="Remove">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-3 items-center">
                <input type="text" placeholder="Document name" value={newDocumentName} onChange={(e) => setNewDocumentName(e.target.value)}
                  className="flex-1 bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-2 text-white placeholder:text-zinc-600 outline-none"
                />
                <input type="file" ref={fileInputRef} accept=".pdf" onChange={handleFileUpload} className="hidden" id="staff-pdf-upload" />
                <label htmlFor="staff-pdf-upload" className="px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-md font-bold cursor-pointer flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Upload PDF
                </label>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Upload PDF documents (contracts, etc.) - Max 10MB</p>
            </div>

            {/* Notes */}
            <div className="pt-6 border-t border-white/5">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">Notes</h2>
              <textarea rows="4" value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                placeholder="Any notes about this staff member..."
              />
            </div>

            {/* Submit */}
            <div className="pt-6 border-t border-white/5 flex gap-4">
              <button type="submit" disabled={loading} data-testid="save-staff-button"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm py-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" strokeWidth={2} />
                {loading ? 'Saving...' : isEdit ? 'Update Coach' : 'Create Coach'}
              </button>
              <Link to="/staff" className="px-6 py-3 bg-white/10 text-white hover:bg-white/20 border border-white/5 rounded-sm transition-all duration-200 uppercase tracking-wide font-bold">
                Cancel
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffForm;
